import React, { useEffect } from 'react';
import simplify from 'simplify-js';
import {
    BaseScribble,
    BaseScribblePathCoordsInput,
    ScribbleContext,
} from '../../ScribbleContext/ScribbleContext';
import { PointerPositionFn } from '../calculatePosition';

interface UseScribbleHandlersOptions<S> {
    ref: React.RefObject<HTMLDivElement>;
    pointerPosition: PointerPositionFn;
    fileId: string;
    useScribble: ScribbleContext<S>['useScribble'];
}

function useScribbleHandlers<S>({
    ref,
    pointerPosition,
    fileId,
    useScribble,
}: UseScribbleHandlersOptions<S>) {
    const { scribble, setCurrentPath: setScribble, createScribble, editScribble } = useScribble();

    const onTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
        const touch = event.touches.item(0);
        if (touch) {
            const position = pointerPosition(ref, touch);
            if (position) {
                setScribble([position]);
            }
        }
    };

    const onMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.button === 0 && ref.current?.contains(event.target as Node)) {
            event.preventDefault();
            const position = pointerPosition(ref, event);
            if (position) {
                setScribble([position]);
            }
        }
    };

    const onMouseMoveEvent = (event: MouseEvent) => {
        const position = pointerPosition(ref, event);
        setScribble((prevPath) => {
            if (prevPath && position) {
                return [...prevPath, position];
            }

            return null;
        });
    };
    useEffect(() => {
        window.addEventListener('mousemove', onMouseMoveEvent);
        return () => {
            window.removeEventListener('mousemove', onMouseMoveEvent);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pointerPosition]);

    const onMouseUpEventCreate = () => {
        setScribble((prevPath) => {
            if (prevPath && prevPath.length >= 3) {
                const path = simplify(prevPath, 2.0, true);
                const newPath: BaseScribblePathCoordsInput = {
                    scribblePathCoords: path.map(({ x, y }) => ({
                        x: Math.round(x),
                        y: Math.round(y),
                    })),
                };
                createScribble(fileId, newPath);
            }

            return null;
        });
    };

    const onMouseUpEventEdit = (scribble: BaseScribble) => () => {
        setScribble((prevPath) => {
            if (prevPath && prevPath.length >= 3) {
                const path = simplify(prevPath, 2.0, true);
                const newPath: BaseScribblePathCoordsInput = {
                    scribblePathCoords: path.map(({ x, y }) => ({
                        x: Math.round(x),
                        y: Math.round(y),
                    })),
                };
                const scribblePaths: BaseScribblePathCoordsInput[] = [
                    ...scribble.scribblePaths.map(({ scribblePathCoords }) => ({
                        scribblePathCoords: scribblePathCoords.map(({ x, y }) => ({ x, y })),
                    })),
                    newPath,
                ];
                editScribble(scribble.id, scribblePaths);
            }

            return null;
        });
    };

    useEffect(() => {
        const onMouseUpEvent = scribble ? onMouseUpEventEdit(scribble) : onMouseUpEventCreate;
        window.addEventListener('mouseup', onMouseUpEvent);
        return () => {
            window.removeEventListener('mouseup', onMouseUpEvent);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pointerPosition, scribble]);

    const onTouchMoveEvent = (event: TouchEvent) => {
        const touch = event.touches.item(0);
        if (touch) {
            const position = pointerPosition(ref, touch);
            setScribble((prevPath) => {
                if (prevPath && position) {
                    return [...prevPath, position];
                }

                return null;
            });
        }
    };
    useEffect(() => {
        window.addEventListener('touchmove', onTouchMoveEvent);
        return () => {
            window.removeEventListener('touchmove', onTouchMoveEvent);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pointerPosition]);

    const onTouchEndEventCreate = (event: TouchEvent) => {
        const touch = event.changedTouches.item(0);
        if (touch) {
            setScribble((prevPath) => {
                if (prevPath && prevPath.length >= 3) {
                    const path = simplify(prevPath, 2.0, true);
                    const newPath: BaseScribblePathCoordsInput = {
                        scribblePathCoords: path.map(({ x, y }) => ({
                            x: Math.round(x),
                            y: Math.round(y),
                        })),
                    };
                    createScribble(fileId, newPath);
                }

                return null;
            });
        }
    };

    const onTouchEndEventEdit = (scribble: BaseScribble) => (event: TouchEvent) => {
        const touch = event.changedTouches.item(0);
        if (touch) {
            setScribble((prevPath) => {
                if (prevPath && prevPath.length >= 3) {
                    const path = simplify(prevPath, 2.0, true);
                    const newPath: BaseScribblePathCoordsInput = {
                        scribblePathCoords: path.map(({ x, y }) => ({
                            x: Math.round(x),
                            y: Math.round(y),
                        })),
                    };
                    const scribblePaths: BaseScribblePathCoordsInput[] = [
                        ...scribble.scribblePaths.map(({ scribblePathCoords }) => ({
                            scribblePathCoords: scribblePathCoords.map(({ x, y }) => ({ x, y })),
                        })),
                        newPath,
                    ];
                    editScribble(scribble.id, scribblePaths);
                }

                return null;
            });
        }
    };

    useEffect(() => {
        const onTouchEndEvent = scribble ? onTouchEndEventEdit(scribble) : onTouchEndEventCreate;
        window.addEventListener('touchend', onTouchEndEvent);
        return () => {
            window.removeEventListener('touchend', onTouchEndEvent);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pointerPosition]);

    return {
        onTouchStart,
        onMouseDown,
    };
}

export default useScribbleHandlers;
